// ABOUTME: W1 — paste a transcript or Q&A + intake metadata, kick off ingestion, watch the job.

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { getJob, ingestTranscript, Job, TranscriptMetadata } from '@/api/ingestion'
import { usePersonas } from '@/hooks/usePersonas'

const INSTITUTIONS = ['ALX', 'ALU', 'ALA', 'FIT']
const EMPTY: TranscriptMetadata = {
  interviewee_name: '', group: '', institution: '', interview_name: '',
  interview_date: '', facilitator: '', notetaker: '', recording_link: '',
}

function errText(err: any): string {
  const d = err?.response?.data?.detail
  if (Array.isArray(d)) return d.map((e: any) => e.msg || JSON.stringify(e)).join('; ')
  return typeof d === 'string' ? d : (err?.message || 'Request failed')
}

export function IngestTranscriptPage() {
  const { data: personas } = usePersonas()
  // Constrain group to the 10-group taxonomy — a free-text typo would mint an 11th coverage segment
  const groups = (personas?.definitions ?? []).map((d) => d.persona_type)
  const [meta, setMeta] = useState<TranscriptMetadata>(EMPTY)
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [job, setJob] = useState<Job | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const pollRef = useRef<number | null>(null)

  const stop = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  useEffect(() => stop, [])

  const set = (k: keyof TranscriptMetadata) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setMeta((m) => ({ ...m, [k]: e.target.value }))

  const poll = (jobId: string) => {
    stop()
    pollRef.current = window.setInterval(async () => {
      try {
        const j = await getJob(jobId)
        setJob(j)
        if (j.status === 'completed' || j.status === 'failed') stop()
      } catch (err: any) {
        // 404 = job gone (server restarted, or pruned from the in-memory store).
        // Treat as terminal so the UI doesn't spin "Working…" forever.
        if (err?.response?.status === 404) {
          setJob((j) => (j ? { ...j, status: 'failed', error: 'Job no longer available (server restarted?).' } : j))
          stop()
        }
        // else: transient network blip — keep polling
      }
    }, 3000)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    for (const k of ['interviewee_name', 'group', 'institution', 'interview_name'] as const) {
      if (!meta[k]?.trim()) { setError(`${k.replace(/_/g, ' ')} is required`); return }
    }
    // Guard the taxonomy-load race: free-text typed before the select swapped in
    // must not submit an off-taxonomy group (the backend rejects it too).
    if (groups.length > 0 && !groups.includes(meta.group)) {
      setError(`Group must be one of the ${groups.length} functional groups — pick from the list.`)
      return
    }
    if (!file && !text.trim()) { setError('Paste a transcript or attach a file'); return }

    setSubmitting(true)
    try {
      const { job_id } = await ingestTranscript(text, meta, file || undefined)
      setJob({ job_id, status: 'queued' } as Job)
      poll(job_id)
    } catch (err) {
      setError(errText(err))
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => { stop(); setJob(null); setError(null); setMeta(EMPTY); setText(''); setFile(null) }

  const running = job && (job.status === 'queued' || job.status === 'running')
  const field = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alx-green/40'

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-alx-blue mb-1">Ingest Interview</h1>
      <p className="text-gray-600 mb-6 text-sm">
        Paste a raw transcript or a structured set of questions &amp; answers. It's structured,
        checked, and loaded into the graph — then evaluated against every hypothesis.
      </p>

      {job ? (
        <div className="border rounded-lg p-5 bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium">Job {job.job_id}</span>
            <StatusBadge status={job.status} />
          </div>
          {running && <p className="text-sm text-gray-600">Extracting &amp; loading — this can take a minute or two…</p>}
          {job.status === 'completed' && job.result && (
            <div className="text-sm space-y-1">
              <p className="text-green-700 font-medium">Ingested.</p>
              <ul className="text-gray-700 list-disc ml-5">
                <li>Interview: {job.result.interview_id}</li>
                <li>Questions: {job.result.questions ?? '—'} · Evidence: {job.result.evidence ?? '—'}</li>
                <li>Group: {job.result.group ?? '—'} · Institution: {job.result.institution ?? '—'}</li>
                <li>Quality score: {job.result.quality_score ?? '—'}/10</li>
              </ul>
            </div>
          )}
          {job.status === 'failed' && (
            <p className="text-sm text-red-600 whitespace-pre-wrap">{job.error || 'Ingestion failed.'}</p>
          )}
          <div className="mt-4 flex items-center gap-3">
            <Button variant="outline" onClick={reset} disabled={!!running}>
              {running ? 'Working…' : 'Ingest another'}
            </Button>
            {job.status === 'completed' && job.result?.interview_id && (
              <Link
                to={`/dashboard/interviews/${job.result.interview_id}`}
                className="text-sm font-medium text-alx-blue hover:underline"
              >
                View interview →
              </Link>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Labeled label="Interviewee *"><Input value={meta.interviewee_name} onChange={set('interviewee_name')} /></Labeled>
            <Labeled label="Interview name *"><Input value={meta.interview_name} onChange={set('interview_name')} /></Labeled>
            <Labeled label="Group *">
              {groups.length > 0 ? (
                <select value={meta.group} onChange={set('group')} className={field}>
                  <option value="">Select…</option>
                  {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              ) : (
                <Input value={meta.group} onChange={set('group')} placeholder="e.g. Learning Designers" />
              )}
            </Labeled>
            <Labeled label="Institution *">
              <select value={meta.institution} onChange={set('institution')} className={field}>
                <option value="">Select…</option>
                {INSTITUTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Labeled>
            <Labeled label="Date"><input type="date" value={meta.interview_date} onChange={set('interview_date')} className={field} /></Labeled>
            <Labeled label="Recording link"><Input value={meta.recording_link} onChange={set('recording_link')} /></Labeled>
            <Labeled label="Facilitator"><Input value={meta.facilitator} onChange={set('facilitator')} /></Labeled>
            <Labeled label="Notetaker"><Input value={meta.notetaker} onChange={set('notetaker')} /></Labeled>
          </div>

          <Labeled label="Transcript or Q&A">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={14}
              placeholder="Paste the full transcript or the questions and answers here…" />
          </Labeled>

          <div className="text-sm text-gray-600">
            …or attach a file:{' '}
            <input type="file" accept=".txt,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {file && <span className="ml-2 text-gray-500">{file.name}</span>}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Ingest'}</Button>
        </form>
      )}
    </div>
  )
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  )
}

function StatusBadge({ status }: { status: Job['status'] }) {
  const cls: Record<Job['status'], string> = {
    queued: 'bg-gray-200 text-gray-700',
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  }
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls[status]}`}>{status}</span>
}
